/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */
import {
  Checkbox,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import React from 'react';
import { CustomTooltip } from '@opengeoweb/shared';
import { useDataExplorerTranslation } from '../../utils/i18n';

interface DataExplorerFilterOptionProps {
  optionId: string;
  filterId: string;
  checked: boolean;
  onClick: (checked: boolean) => void;
  value: string;
  count: number;
}

export const DataExplorerFilterOption: React.FC<
  DataExplorerFilterOptionProps
> = ({
  optionId,
  filterId,
  checked,
  onClick,
  value,
  count,
}: DataExplorerFilterOptionProps) => {
  const { t } = useDataExplorerTranslation();
  return (
    <ListItem
      disablePadding
      sx={{
        width: '100%',
        height: '100%',
        borderBottom: '1px solid #E0E0E0',
      }}
    >
      <ListItemButton
        disableGutters
        onClick={() => onClick(!checked)}
        role={undefined}
        style={{
          padding: 0,
          height: '100%',
        }}
      >
        <ListItemIcon sx={{ width: '42px' }}>
          <Checkbox
            checked={checked}
            data-testid={`dataExplorerFilterOption-${optionId}`}
            disableRipple
            name={optionId}
            slotProps={{
              input: { 'aria-labelledby': optionId, 'aria-label': optionId },
            }}
            tabIndex={-1}
          />
        </ListItemIcon>
        <ListItemText
          id={optionId}
          primary={
            filterId === 'contacts.addresses.country' ? (
              <CustomTooltip title={t(value)}>
                <Typography
                  data-testid={`dataExplorerFilterOptionValue-${value}`}
                  sx={{
                    float: 'left',
                    fontSize: '12px',
                    maxWidth: 'calc(100% - 44px)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {t(value)}
                </Typography>
              </CustomTooltip>
            ) : (
              <CustomTooltip title={value}>
                <Typography
                  sx={{
                    float: 'left',
                    fontSize: '12px',
                    maxWidth: 'calc(100% - 44px)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {value}
                </Typography>
              </CustomTooltip>
            )
          }
          secondary={
            <Typography
              sx={{ float: 'right', fontSize: '12px', paddingRight: '12px' }}
            >
              {count}
            </Typography>
          }
        />
      </ListItemButton>
    </ListItem>
  );
};
